/**
 * MNPI / restricted-list engine helpers (migration 0011: restricted_list_entries
 * + wall_crossing_events). Pure, deterministic — no I/O, no AI. Mirrors the
 * political-contributions de-minimis helper shape in compliance.ts.
 *
 * screenName() is the "screen a name" box on the restricted-list page: it must
 * be a fast, local, rules-only check (never an AI call — build doc §17.6 bans
 * AI-only compliance gates without a deterministic rules layer underneath).
 */

export const MNPI_ENGINE_VERSION = "mnpi-rules-v1.0";

/** Common legal-entity suffixes stripped during issuer-name normalization. */
const LEGAL_SUFFIXES = new Set([
  "inc",
  "incorporated",
  "corp",
  "corporation",
  "co",
  "company",
  "llc",
  "ltd",
  "limited",
  "lp",
  "llp",
  "plc",
  "sa",
  "nv",
  "ag",
  "gmbh",
]);

/**
 * Normalize an issuer name for matching: lowercase, expand "&", strip
 * punctuation, strip trailing legal-entity suffixes (inc/corp/llc/ltd/…),
 * collapse whitespace. Deterministic — same input always produces the same
 * output.
 */
export function normalizeIssuer(name: string): string {
  const cleaned = name
    .toLowerCase()
    .replace(/&/g, " and ")
    // Drop periods first so dotted acronyms collapse to one token
    // ("L.L.C." -> "llc") instead of splitting into stray letters.
    .replace(/\./g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  const words = cleaned.split(" ");
  while (words.length > 1 && LEGAL_SUFFIXES.has(words[words.length - 1] ?? "")) {
    words.pop();
  }
  return words.join(" ");
}

export type ListType = "restricted" | "watch";
export type EntryStatus = "active" | "removed";

/** Minimal shape screenName() needs — a subset of the restricted_list_entries row. */
export interface RestrictedEntryInput {
  id: string;
  issuer_name: string;
  ticker: string | null;
  list_type: ListType;
  reason: string;
  status: EntryStatus;
}

export type ScreenMatchType = "ticker" | "issuer_exact" | "issuer_contains";

export interface ScreenMatch {
  entry: RestrictedEntryInput;
  matchType: ScreenMatchType;
}

export interface ScreenResult {
  query: string;
  normalizedQuery: string;
  hit: boolean;
  matches: ScreenMatch[];
}

/**
 * Screen a free-text name/ticker query against the active restricted/watch
 * list. Matches on: exact ticker (case-insensitive), exact normalized issuer
 * name, or a normalized-issuer substring match in either direction. Only
 * `status === 'active'` entries are considered — removed entries never hit.
 */
export function screenName(query: string, entries: RestrictedEntryInput[]): ScreenResult {
  const trimmed = query.trim();
  const normalizedQuery = normalizeIssuer(trimmed);
  const tickerQuery = trimmed.toUpperCase();
  const matches: ScreenMatch[] = [];

  for (const entry of entries) {
    if (entry.status !== "active") continue;

    if (entry.ticker && tickerQuery.length > 0 && entry.ticker.toUpperCase() === tickerQuery) {
      matches.push({ entry, matchType: "ticker" });
      continue;
    }

    if (normalizedQuery.length === 0) continue;
    const normalizedIssuer = normalizeIssuer(entry.issuer_name);
    if (normalizedIssuer.length === 0) continue;

    if (normalizedIssuer === normalizedQuery) {
      matches.push({ entry, matchType: "issuer_exact" });
    } else if (
      normalizedIssuer.includes(normalizedQuery) ||
      normalizedQuery.includes(normalizedIssuer)
    ) {
      matches.push({ entry, matchType: "issuer_contains" });
    }
  }

  return { query: trimmed, normalizedQuery, hit: matches.length > 0, matches };
}

export type WallEventType =
  | "wall_crossing_granted"
  | "wall_crossing_denied"
  | "access_review"
  | "list_screen";

/** Minimal shape wallLogStats() needs — a subset of the wall_crossing_events row. */
export interface WallEventInput {
  event_type: WallEventType;
  occurred_at: string;
}

export interface WallLogStats {
  total: number;
  granted: number;
  denied: number;
  reviews: number;
  screens: number;
  /** ISO timestamp of the most recent event, or null if the log is empty. */
  lastEventAt: string | null;
}

/** Counts by event type + the most recent event timestamp, for the log header. */
export function wallLogStats(events: WallEventInput[]): WallLogStats {
  let granted = 0;
  let denied = 0;
  let reviews = 0;
  let screens = 0;
  let lastEventAt: string | null = null;

  for (const e of events) {
    switch (e.event_type) {
      case "wall_crossing_granted":
        granted++;
        break;
      case "wall_crossing_denied":
        denied++;
        break;
      case "access_review":
        reviews++;
        break;
      case "list_screen":
        screens++;
        break;
    }
    if (lastEventAt === null || e.occurred_at > lastEventAt) lastEventAt = e.occurred_at;
  }

  return { total: events.length, granted, denied, reviews, screens, lastEventAt };
}
